/* eslint-disable camelcase */
import React, { useMemo, useState } from 'react';
import { FieldArray, Form, Formik } from 'formik';
import _ from 'lodash';
import moment from 'moment';
import { useSelector } from 'react-redux';
import Button from '../../../components/button';
import FormSelect from '../../../components/dropdown/formSelect';
import InputText from '../../../components/input-text/inputText';
import Spinners from '../../../components/spinner';
import Heading from '../../../components/typography';
import { COLORS } from '../../../constants/style/color';
import {
  useGetAlertTypes,
  useGetServicedMyCompanies,
  useGetSupportContactByCompanyId,
  useWealthlaneCustomerApi,
} from '../../../hooks';
import NoDataFound from '../../../components/no-data-found';
import usePostSupportContacts from '../../../hooks/post/usePostSupportContacts';
import { selectAuthRoutesList } from '../../configuration/routeSlice';
import { getFilteredPermissionData } from '../../../utils';
import { ALERT_SUBSCRIPTION_VALIDATION_SCHEMA } from '../../../utils/validations/validations';
import { PageHeader, PageWrapper } from '../company-profile/profile.styled';
import {
  AlertSubscriptionCard,
  SubscriptionTableHeader,
  SubscriptionTableRow,
} from './alertSubscription.styled';
import type { Wealthlane_CustomerService_Dtos_SupportContact_SaveSupportContactsRequestDto } from '../../../services/wealthlane-customer-services/models/Wealthlane_CustomerService_Dtos_SupportContact_SaveSupportContactsRequestDto';

interface SupportContactRow {
  id?: string | null;
  email: string;
  alertType: string;
  subscribedOn?: string | null;
}

interface AlertSubscriptionFormValues {
  sourceCompanyId: string;
  alertType: string;
  supportContacts: SupportContactRow[];
}

const EMPTY_ROW: SupportContactRow = {
  id: undefined,
  email: '',
  alertType: '',
  subscribedOn: null,
};

const AlertSubscription = (): JSX.Element => {
  const { handleRequest } = useWealthlaneCustomerApi();
  const userAuthList = useSelector(selectAuthRoutesList);
  const { nonFieldLevelPermissionsGroups } = getFilteredPermissionData(
    userAuthList?.auth?.grantedPolicies ?? {},
    'CustomerService.Customer.'
  );
  const hasPermission = Object.keys(nonFieldLevelPermissionsGroups);
  const hasManageAlertSubscription = hasPermission.includes('ManageAlertSubscription');

  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');

  const { data: companies, isLoading: isCompaniesLoading } =
    useGetServicedMyCompanies(handleRequest);
  const { data: alertTypes, isLoading: isAlertTypesLoading } = useGetAlertTypes(handleRequest);
  const { data: supportContacts, isLoading: isContactsLoading } = useGetSupportContactByCompanyId(
    handleRequest,
    selectedCompanyId,
    hasManageAlertSubscription
  );

  const noopClose = (): void => { };
  const { mutate, isLoading: isSaving } = usePostSupportContacts(handleRequest, noopClose);

  const companyOptions = useMemo(
    () => companies?.map((c) => ({ label: c.label, value: c.value })) ?? [],
    [companies]
  );

  const initialValues: AlertSubscriptionFormValues = useMemo(() => {
    if (supportContacts != null && supportContacts.length > 0) {
      const contacts = supportContacts.map((contact) => ({
        id: contact.id ?? null,
        email: contact.email ?? '',
        alertType: (contact as any).alertType ?? alertTypes?.[0]?.value ?? '',
        subscribedOn: (contact as any).subscribedOn ?? null,
      }));
      return {
        sourceCompanyId: selectedCompanyId,
        alertType: contacts[0]?.alertType ?? '',
        supportContacts: contacts,
      };
    }
    return {
      sourceCompanyId: selectedCompanyId,
      alertType: '',
      supportContacts: [],
    };
  }, [supportContacts, alertTypes, selectedCompanyId]);

  const handleCompanyChange = (companyId: string): void => {
    setSelectedCompanyId(companyId ?? '');
  };

  const handleSubmit = (values: AlertSubscriptionFormValues): void => {
    if (!values.sourceCompanyId) return;
    const validContacts = values.supportContacts.filter((item) => item.email.trim() !== '');
    const payload: Wealthlane_CustomerService_Dtos_SupportContact_SaveSupportContactsRequestDto = {
      sourceCompanyId: values.sourceCompanyId,
      supportContacts: validContacts.map(({ id, email, alertType }) => ({
        id: id ?? undefined,
        email,
        alertType: alertType || null,
      })),
    };
    mutate(payload);
  };

  if (!hasManageAlertSubscription) {
    return (
      <PageWrapper id='alertSubscriptionPageWrapper'>
        <PageHeader id='alertSubscriptionPageHeader'>
          <Heading variant='h3' title='Manage Subscription' id='alertSubscriptionPageTitle' />
          <Heading
            variant='body-s'
            title='You do not have permission to manage alert subscriptions.'
            color={COLORS.Gray}
            id='alertSubscriptionNoPermission'
          />
        </PageHeader>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper id='alertSubscriptionPageWrapper'>
      <PageHeader id='alertSubscriptionPageHeader'>
        <Heading variant='h3' title='Manage Subscription' id='alertSubscriptionPageTitle' />
        <Heading
          variant='body-s'
          title='Manage alert subscription contacts for your companies'
          color={COLORS.Gray}
          id='alertSubscriptionPageDescription'
        />
      </PageHeader>

      <AlertSubscriptionCard id='alertSubscriptionCard'>
        <div className='company-select' id='companySelectSection'>
          <div className='grid grid-cols-12 gap-4'>
            <div className='col-span-4'>
              <FormSelect
                label='Company'
                name='companySelector'
                id='companySelector'
                placeholder='Select Company'
                options={companyOptions}
                isLoading={isCompaniesLoading}
                isFormik={false}
                values={{ companySelector: selectedCompanyId }}
                setFieldValue={(_name: string, value: any) => {
                  handleCompanyChange(value);
                }}
                onChange={() => { }}
                errors={{}}
                touched={{}}
                required
              />
            </div>
          </div>
        </div>

        {selectedCompanyId === '' && (
          <NoDataFound
            message='Please select a company to manage subscriptions.'
            id='noCompanySelected'
          />
        )}

        {selectedCompanyId !== '' && isContactsLoading && (
          <div className='py-6' id='alertSubscriptionLoading'>
            <Spinners containerClassName='h-40' />
          </div>
        )}

        {selectedCompanyId !== '' && !isContactsLoading && (
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={ALERT_SUBSCRIPTION_VALIDATION_SCHEMA}
            onSubmit={handleSubmit}>
            {({ touched, handleChange, setFieldValue, errors, values }) => {
              return (
                <Form id='alertSubscriptionForm'>
                  <FieldArray
                    name='supportContacts'
                    render={(arrayHelpers) => {
                      return (
                        <div id='subscriptionFieldArray'>
                          <div className='section-header' id='subscriptionSectionHeader'>
                            <div></div>
                            <Button
                              title='Add Subscription'
                              type='button'
                              variant='secondary-dark'
                              icon='add_circle_outline'
                              id='addSubscriptionButton'
                              onClick={() => {
                                arrayHelpers.push({ ...EMPTY_ROW });
                              }}
                            />
                          </div>

                          {values.supportContacts.length === 0 ? (
                            <NoDataFound
                              message='No subscriptions found for this company.'
                              id='noSubscriptions'
                            />
                          ) : (
                            <>
                              <SubscriptionTableHeader id='subscriptionTableHeader'>
                                <Heading
                                  variant='body-xs'
                                  title='Alert Type'
                                  color={COLORS.Gray}
                                  id='headerAlertType'
                                />
                                <Heading
                                  variant='body-xs'
                                  title='Email'
                                  color={COLORS.Gray}
                                  id='headerEmail'
                                />
                                <Heading
                                  variant='body-xs'
                                  title='Subscribed On'
                                  color={COLORS.Gray}
                                  id='headerSubscribedOn'
                                />
                                <div />
                              </SubscriptionTableHeader>

                              <div id='subscriptionRows'>
                                {values.supportContacts.map((item, index) => (
                                  <SubscriptionTableRow key={index} id={`subscriptionRow.${index}`}>
                                    <div className='select-cell' id={`alertTypeContainer.${index}`}>
                                      <FormSelect
                                        name={`supportContacts.${index}.alertType`}
                                        id={`supportContacts.${index}.alertType`}
                                        containerClassName='mb-4'
                                        placeholder='Select Type'
                                        options={alertTypes}
                                        isLoading={isAlertTypesLoading}
                                        values={values}
                                        arrValue={item.alertType}
                                        setFieldValue={setFieldValue}
                                        onChange={handleChange}
                                        errors={errors}
                                        touched={touched}
                                        errorValue={
                                          (errors?.supportContacts as any)?.[index]?.alertType
                                        }
                                        touchedValue={
                                          (touched?.supportContacts as any)?.[index]?.alertType
                                        }
                                        required
                                      />
                                    </div>
                                    <div id={`emailContainer.${index}`}>
                                      <InputText
                                        type='email'
                                        name={`supportContacts.${index}.email`}
                                        id={`supportContacts.${index}.email`}
                                        placeholder='Enter email address'
                                        className='mb-4'
                                        onChange={handleChange}
                                        errorValue={
                                          (errors?.supportContacts as any)?.[index]?.email
                                        }
                                        touchedValue={
                                          (touched?.supportContacts as any)?.[index]?.email
                                        }
                                        errors={errors}
                                        touched={touched}
                                        required
                                      />
                                    </div>
                                    <div
                                      className='date-cell'
                                      id={`subscribedOnContainer.${index}`}>
                                      <Heading
                                        title={
                                          item.subscribedOn != null
                                            ? moment(item.subscribedOn).format('M/D/YYYY')
                                            : '-'
                                        }
                                        variant='body-s'
                                        id={`supportContacts.${index}.subscribedOn`}
                                      />
                                    </div>
                                    <div className='action-cell' id={`actionsContainer.${index}`}>
                                      <span
                                        className='material-symbols-outlined delete-icon'
                                        role='button'
                                        id={`deleteSubscriptionBtn.${index}`}
                                        onClick={() => {
                                          arrayHelpers.remove(index);
                                        }}>
                                        delete
                                      </span>
                                    </div>
                                  </SubscriptionTableRow>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    }}
                  />

                  {(values.supportContacts.length > 0 ||
                    !_.isEqual(values, initialValues)) && (
                    <div className='section-actions' id='saveButtonContainer'>
                      <Button
                        variant='primary'
                        title='Save Changes'
                        type='submit'
                        loading={isSaving}
                        disabled={isSaving || _.isEqual(values, initialValues)}
                        id='saveSubscriptionButton'
                      />
                    </div>
                  )}
                </Form>
              );
            }}
          </Formik>
        )}
      </AlertSubscriptionCard>
    </PageWrapper>
  );
};

export default AlertSubscription;
